import type { AppLocationCodec } from "@/navigation/appLocation";
import { NAV_KEYS } from "@/navigation/keys";

export type DisclosureLocation = {
  caseId?: string;
  focus?: string;
};

function readString(params: Record<string, string | undefined>, key: string): string | undefined {
  return params[key]?.trim() || undefined;
}

export function normalizeDisclosure(loc: DisclosureLocation): DisclosureLocation {
  if (loc.caseId) {
    return { caseId: loc.caseId };
  }
  const next: DisclosureLocation = {};
  if (loc.focus) next.focus = loc.focus;
  return next;
}

function fromParams(params: Record<string, string | undefined>): DisclosureLocation {
  const caseId = readString(params, "caseId");
  if (caseId) {
    return { caseId };
  }
  const next: DisclosureLocation = {};
  const focus = readString(params, NAV_KEYS.focus);
  if (focus) next.focus = focus;
  return normalizeDisclosure(next);
}

function fromUrl(pathname: string, searchParams: URLSearchParams): DisclosureLocation {
  const params: Record<string, string | undefined> = Object.fromEntries(searchParams);
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] === "disclosures" && segments[1]) {
    params.caseId = decodeURIComponent(segments[1]);
  }
  return fromParams(params);
}

function toParams(loc: DisclosureLocation): Record<string, string> {
  const n = normalizeDisclosure(loc);
  const out: Record<string, string> = {};
  if (n.caseId) {
    out.caseId = n.caseId;
    return out;
  }
  if (n.focus) out[NAV_KEYS.focus] = n.focus;
  return out;
}

function toUrl(loc: DisclosureLocation): string {
  const n = normalizeDisclosure(loc);
  if (n.caseId) {
    return `/disclosures/${encodeURIComponent(n.caseId)}`;
  }
  const qs = new URLSearchParams(toParams(n)).toString();
  return `/disclosures${qs ? `?${qs}` : ""}`;
}

export const disclosureCodec: AppLocationCodec<DisclosureLocation> = {
  appId: "disclosures",
  fromUrl,
  fromParams,
  toParams,
  toUrl,
};

export function parseDisclosureFromRoute(
  resourceId: string | undefined,
  searchParams: URLSearchParams,
): DisclosureLocation {
  const params: Record<string, string | undefined> = Object.fromEntries(searchParams);
  if (resourceId) {
    params.caseId = decodeURIComponent(resourceId);
  }
  return fromParams(params);
}

export function serializeDisclosureToOsParams(loc: DisclosureLocation): Record<string, string> {
  return toParams(loc);
}
